import { useEffect, useState } from "react";
import { api, peso } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { Card } from "@/components/ui/card";
import { Coins, Receipt, Bank, TrendUp } from "@phosphor-icons/react";

function Stat({ label, v, Icon, tid }) {
  return (
    <Card className="bg-[#141414] border-zinc-800 p-6">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">{label}</p>
        <Icon size={18} className="text-zinc-500" />
      </div>
      <p className="mt-3 font-mono text-3xl" data-testid={TID.metric(tid)}>{v}</p>
    </Card>
  );
}

export default function Finance() {
  const [data, setData] = useState(null);
  const [payments, setPayments] = useState([]);
  useEffect(() => {
    api.get("/dashboard/finance").then(r => setData(r.data));
    api.get("/payments?limit=20").then(r => setPayments(r.data));
  }, []);
  if (!data) return <div className="text-zinc-500">Loading…</div>;
  const max = Math.max(1, ...data.last_7_days.map(d => d.revenue));
  return (
    <div className="space-y-8">
      <header>
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-500">Finance</p>
        <h1 className="font-heading text-4xl font-black tracking-tighter mt-1">
          Money <span className="text-amber-300">in motion.</span>
        </h1>
      </header>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Revenue Today" v={peso(data.revenue_today)} Icon={Coins} tid="fin-today" />
        <Stat label="Revenue Total" v={peso(data.total_revenue)} Icon={TrendUp} tid="fin-total" />
        <Stat label="Pending" v={peso(data.pending_collections)} Icon={Receipt} tid="fin-pending" />
        <Stat label="Transactions" v={data.transaction_count} Icon={Bank} tid="fin-tx" />
      </div>

      <Card className="bg-[#141414] border-zinc-800 p-6">
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Last 7 Days</p>
        <div className="mt-6 flex items-end gap-3 h-40">
          {data.last_7_days.map((d) => (
            <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-2">
              <div
                className="w-full bg-emerald-500/30 border border-emerald-500/50 rounded-sm"
                style={{ height: `${(d.revenue / max) * 100}%`, minHeight: "2px" }}
                title={peso(d.revenue)}
              />
              <span className="text-[10px] font-mono text-zinc-500">{d.date}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card className="bg-[#141414] border-zinc-800 p-6">
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Recent Payments</p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-zinc-500 text-xs font-mono uppercase">
                <th className="py-2 pr-4">Reference</th><th className="py-2 pr-4">Amount</th>
                <th className="py-2 pr-4">Method</th><th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">When</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {payments.map(p => (
                <tr key={p.id}>
                  <td className="py-2 pr-4 font-mono text-xs">{p.reference}</td>
                  <td className="py-2 pr-4 font-mono">{peso(p.amount)}</td>
                  <td className="py-2 pr-4 capitalize">{p.method}</td>
                  <td className="py-2 pr-4">
                    <span className={`inline-block px-2 py-0.5 text-xs rounded border ${
                      p.status === "confirmed" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                      : "bg-amber-500/15 text-amber-300 border-amber-500/30"}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-zinc-400 text-xs">{new Date(p.confirmed_at || p.created_at).toLocaleString()}</td>
                </tr>
              ))}
              {payments.length === 0 && (
                <tr><td colSpan={5} className="py-6 text-center text-zinc-500">No payments yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
